"use client";

import { Columns4Icon, Grid2X2Icon, RectangleVerticalIcon, SpaceIcon, SquareIcon } from "lucide-react";
import useSWR from "swr";
import { collectionBrowseAction } from "@/actions/collection";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NULL_STR } from "@/lib/constants";
import { useStoreBuilderContext } from "@/ui/admin/store-builder/store-builder-context";
import { useDebounce } from "@/ui/hooks/use-debounced-value";
import { useRouterRefresh } from "@/ui/hooks/use-router-refresh";
import { Label } from "@/ui/shadcn/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui/shadcn/select";
import { StatefulSlider } from "@/ui/stateful-slider";
import { YnsLink } from "@/ui/yns-link";
import { useStartGlobalTransition } from "@/zustand/admin";
import type { YnsComponentForm } from "../types";
import type { ProductGrid } from "./product-grid";

export const ProductGridForm: YnsComponentForm<typeof ProductGrid> = ({ data }) => {
	const { data: collections } = useSWR("getCollectionsAction", () => collectionBrowseAction(), {
		keepPreviousData: true,
	});

	const context = useStoreBuilderContext();
	const refresh = useRouterRefresh();
	const startGlobalTransition = useStartGlobalTransition();

	const handleFirstChange = useDebounce(async (v: number[]) => {
		return startGlobalTransition(async () => {
			await context.saveSelectedPageSectionField({
				fieldPath: "first",
				value: v[0],
			});
			await refresh();
		});
	}, 200);
	const handleColumnsChange = useDebounce(async (v: number[]) => {
		return startGlobalTransition(async () => {
			await context.saveSelectedPageSectionField({
				fieldPath: "columns",
				value: v[0],
			});
			await refresh();
		});
	}, 200);

	return (
		<div className="grid gap-4 relative pb-20">
			<div>
				<Label className="flex items-center gap-2 mt-2 w-full">
					<Tabs
						defaultValue={data.productLayout}
						className="w-full"
						onValueChange={(value) => {
							context.saveSelectedPageSectionField({
								fieldPath: "productLayout",
								value,
							});
						}}
					>
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="grid">
								<Grid2X2Icon className="w-4 h-4 mr-1" />
								Grid
							</TabsTrigger>
							<TabsTrigger value="carousel">
								<Columns4Icon className="w-4 h-4 mr-1" />
								Carousel
							</TabsTrigger>
						</TabsList>
					</Tabs>
				</Label>
				{/* <p className="text-[0.8rem] mt-1 text-muted-foreground">text</p> */}
			</div>

			<Label className="grid gap-2 mt-2 w-full">
				<span>Aspect Ratio</span>
				<Tabs
					defaultValue={data.aspectRatio ?? "16/9"}
					className="w-full"
					onValueChange={(value) => {
						context.saveSelectedPageSectionField({
							fieldPath: "aspectRatio",
							value: value,
						});
					}}
				>
					<TabsList className="grid w-full grid-cols-3">
						<TabsTrigger value="1/1">
							<SquareIcon className="size-4" />
							1:1
						</TabsTrigger>
						<TabsTrigger value="9/16">
							<RectangleVerticalIcon className="size-4" />
							9:16
						</TabsTrigger>
						<TabsTrigger value="3/4">
							<RectangleVerticalIcon className="size-4 scale-x-125" />
							3:4
						</TabsTrigger>
					</TabsList>
				</Tabs>
			</Label>

			<Label className="grid gap-2 mt-2">
				<span>Collection</span>
				<Select
					key={collections?.data?.length}
					defaultValue={data.collection === null ? NULL_STR : data.collection}
					disabled={!collections?.data?.length}
					onValueChange={async (value) => {
						await context.saveSelectedPageSectionField({
							fieldPath: "collection",
							value,
						});
					}}
				>
					<SelectTrigger className="w-full">
						<SelectValue placeholder="Select a collection" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={NULL_STR}>(None)</SelectItem>
						{collections?.data?.map((collection) => (
							<SelectItem value={collection.slug} key={collection.slug}>
								{collection.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</Label>

			<Label className="grid gap-2 mt-2">
				<span>Number of products to display</span>
				<StatefulSlider defaultValue={[data.first]} min={1} max={24} onValueChange={handleFirstChange} />
			</Label>

			<Label className="grid gap-2 mt-2">
				<span>Number of columns</span>
				<StatefulSlider
					defaultValue={[data.columns || 3]}
					min={3}
					max={4}
					onValueChange={handleColumnsChange}
				/>
			</Label>

			<Label className="grid gap-2 mt-2 w-full">
				<span>Gap between columns</span>
				<Tabs
					defaultValue={data.columnGap ?? "sm"}
					className="w-full"
					onValueChange={(value) => {
						context.saveSelectedPageSectionField({
							fieldPath: "columnGap",
							value: value,
						});
					}}
				>
					<TabsList className="grid w-full grid-cols-3">
						<TabsTrigger value="sm">
							<SpaceIcon className="size-4 scale-x-75" />
							Small
						</TabsTrigger>
						<TabsTrigger value="md">
							<SpaceIcon className="size-4" />
							Medium
						</TabsTrigger>
						<TabsTrigger value="lg">
							<SpaceIcon className="size-4 scale-x-125" />
							Large
						</TabsTrigger>
					</TabsList>
				</Tabs>
			</Label>

			<Label className="grid gap-2 mt-2">
				<span>Should show pagination</span>
				<Select
					defaultValue={data.pagination?.toString() ?? "false"}
					onValueChange={async (value) => {
						return startGlobalTransition(async () => {
							await context.saveSelectedPageSectionField({
								fieldPath: "pagination",
								value,
							});
							await refresh();
						});
					}}
				>
					<SelectTrigger className="w-full">
						<SelectValue placeholder="" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={"true"}>Enable pagination</SelectItem>
						<SelectItem value={"false"}>Disable pagination</SelectItem>
					</SelectContent>
				</Select>
			</Label>

			<Button asChild className="w-max mt-8" variant="outline">
				<YnsLink href="/admin/products">Go to Add Products</YnsLink>
			</Button>
		</div>
	);
};
